import { t } from "i18next";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import BackEndService from "../../api/BackEndService";
import CustomerService from "../../api/CustomerService";
import PropertyService from "../../api/PropertyService";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { generateUUIDFromID } from "../../services/UUIDGeneratorService";
import { RootState } from "../../store";
import { searchActions } from "../../store/search-slice";
import { creatingTaskActions } from "../../store/task-slice";
import { SevDeskCustomer } from "../../types/customer";
import { sortCustomers } from "../../utils/sortCustomers";
import { List } from "../List/List";
import PullToRefresh from "../PullToRefresh/PullToRefresh";
import { SubMenu } from "../SubMenu/SubMenu";

import styles from "./task-form.module.css";

export const StepOne = (): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [initialCustomers, setInitialCustomers] = useState([]);
  const [isNavigating, setIsNavigating] = useState(false);
  const searchValue = useSelector((state: RootState) => state.search.value);

  // Fetch fresh list of sevdesk customers
  useEffect(() => {
    fetchCustomers();
  }, []);

  // Fetch sevdesk customers list
  const fetchCustomers = async () => {
    try {
      const customers = await CustomerService.GetSevDeskCustomers();

      const sortedCustomers = sortCustomers(customers);

      setCustomers(sortedCustomers);
      setInitialCustomers(sortedCustomers);
    } catch (error) {
      console.log(error);
    }
  };

  // Fetch customer properties by customer id
  const fetchCustomerProperties = async (customerId: string) => {
    const _customerId = generateUUIDFromID(customerId?.toString()!);

    try {
      const sevdeskProperties = await BackEndService.GetObjects(customerId); //sevdesk objects
      const properties = await PropertyService.GetProperties({
        "filter[customerId]": _customerId,
      }); //backend objects

      const missingSevdeskProperties = sevdeskProperties.data.filter(
        (sevdeskProperty) =>
          !properties.data.some(
            (property) =>
              property.id === generateUUIDFromID(sevdeskProperty.id.toString())
          )
      );

      dispatch(
        creatingTaskActions.setCustomerProperties({
          customerProperties: [...properties.data, ...missingSevdeskProperties],
        })
      );

      navigate("/customerinsight/second-step");
    } catch (error) {
      console.log(error);
    }
  };

  const onSelectCustomer = (customer: Customer) => {
    setIsNavigating(true);
    dispatch(creatingTaskActions.setCustomer({ customer: customer }));
    dispatch(
      creatingTaskActions.setAdditionalDataCustomer({
        customer: {
          id: customer.id,
          name: customer.name,
        },
      })
    );

    fetchCustomerProperties(customer.id);

    dispatch(searchActions.setIsExpanded(false));
    dispatch(searchActions.setSearchValue(""));
  };

  const handlePullToRefresh = (callback: Function) => {
    fetchCustomers()
      .then((customers) => {
        callback();
      })
      .catch((error) => {
        callback(error);
      });
  };

  const handleGoBack = () => {
    dispatch(creatingTaskActions.resetAppointment());
    navigate("/");
  };

  useEffect(() => {
    if (!isNavigating) {
      if (searchValue.length >= 3) {
        const lowerCaseSearchValue = searchValue.toLowerCase();

        setCustomers(
          initialCustomers.filter((customer: SevDeskCustomer) => {
            const search = lowerCaseSearchValue;

            const matchesCustomer =
              customer.name?.toLowerCase().includes(search) ||
              customer.surename?.toLowerCase().includes(search) ||
              customer.familyname?.toLowerCase().includes(search) ||
              `${customer.surename ?? ""} ${customer.familyname ?? ""}`
                .toLowerCase()
                .includes(search);

            const matchesAddress = customer.addresses?.some((address) => {
              if (address.category?.id !== "47") {
                return false;
              }

              const fullAddress =
                `${address.street ?? ""} ${address.zip ?? ""} ${address.city ?? ""}`.toLowerCase();

              return (
                address.street?.toLowerCase().includes(search) ||
                address.zip?.toLowerCase().includes(search) ||
                address.city?.toLowerCase().includes(search) ||
                fullAddress.includes(search)
              );
            });

            return matchesCustomer || matchesAddress;
          })
        );
      } else if (searchValue.length === 0) {
        setCustomers(initialCustomers);
      }
    }
  }, [searchValue, initialCustomers, isNavigating]);

  return (
    <AppLayout
      className={styles["tasks__list"]}
      withBackButton={false}
      onGoBack={handleGoBack}
    >
      <SubMenu showSearchButton={true}>{t("EnterTask")}</SubMenu>
      <PullToRefresh
        className={styles["customer-refresh"]}
        onRefresh={handlePullToRefresh}
      >
        <List
          subtitle={t("CustomerSelect")}
          data={customers.map((customer: SevDeskCustomer) => {
            const address = customer.addresses?.find(
              (address) => address.category?.id === "47"
            );

            return {
              id: customer.id.toString(),
              name:
                customer.name ||
                `${customer.surename ?? ""} ${customer.familyname ?? ""}`.trim(),
              address: address
                ? `${address.street ?? ""}, ${address.zip ?? ""} ${address.city ?? ""}`
                : "",
            };
          })}
          onItemSelected={(customer: any) => {
            onSelectCustomer(customer);
          }}
          boldName
        />
      </PullToRefresh>
    </AppLayout>
  );
};
